import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { consoleRhythm, consoleTokens } from './consoleTokens';

export interface ConsolePageProps {
  /** Report/page title, leftmost in the command bar. */
  title: React.ReactNode;
  /** FilterTokens and related controls after the title. */
  tokens?: React.ReactNode;
  /** Right-aligned command bar content (kbd hints, export button, ⓘ). */
  commandBarRight?: React.ReactNode;
  /** Usually a ConsoleStatStrip. */
  statStrip?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Page shell: command bar → scroll region (stat strip → content). The page
 * fills its parent's height; the region below the command bar is the single
 * scroll container for BOTH axes, so a wide table scrolls horizontally while
 * the column header stays sticky vertically (CSS sticky needs one container
 * to own both axes). The stat strip lives inside the region — it scrolls
 * away vertically (headlines are for arrival, not reference) but pins to the
 * left edge so horizontal scrolling never strands it.
 */
export const ConsolePage: React.FC<ConsolePageProps> = ({
  title,
  tokens,
  commandBarRight,
  statStrip,
  children,
}) => (
  <Box
    sx={{
      bgcolor: '#FFFFFF',
      color: consoleTokens.ink,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}
  >
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        bgcolor: '#FFFFFF',
        borderBottom: `1px solid ${consoleTokens.line}`,
        flex: 'none',
        // Wrap to extra rows at narrow widths — clipped controls are worse
        // than a taller bar.
        flexWrap: 'wrap',
        gap: 1,
        minHeight: consoleRhythm.commandBar,
        px: 2,
        py: 0.75,
        zIndex: 3,
      }}
    >
      <Typography
        component="h1"
        sx={{
          alignItems: 'center',
          color: consoleTokens.ink,
          display: 'flex',
          flexShrink: 0,
          fontSize: '0.8125rem',
          fontWeight: 600,
          mr: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </Typography>
      {tokens}
      <Box sx={{ flex: 1 }} />
      {commandBarRight}
    </Stack>
    {/* contain:paint forces the compositor to honor the overflow clip —
        without it Chrome lets row text bleed over the sticky header. */}
    <Box sx={{ contain: 'paint', flex: 1, minHeight: 0, overflow: 'auto' }}>
      {statStrip && (
        <Box sx={{ left: 0, minWidth: '100%', position: 'sticky', width: 'fit-content' }}>
          {statStrip}
        </Box>
      )}
      {children}
    </Box>
  </Box>
);
